'use client';

import type { UrlObject } from 'node:url';
import { useNetwork } from '@/lib/contexts/NetworkContext';
import Link, { type LinkProps } from 'next/link';
import React from 'react';

interface NetworkLinkProps extends Omit<LinkProps, 'href'> {
  href: string | (Omit<UrlObject, 'query'> & { query?: Record<string, any> });
  children: React.ReactNode;
  className?: string;
  // Add any other props you expect Link to normally accept
  [key: string]: any;
}

export const NetworkLink = React.forwardRef<HTMLAnchorElement, NetworkLinkProps>(
  ({ href, children, ...props }, ref) => {
    const { network } = useNetwork();

    let newHref: typeof href;

    if (typeof href === 'string') {
      const url = new URL(
        href,
        typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
      ); // Base is needed for relative paths
      url.searchParams.set('network', network);
      newHref = `${url.pathname}${url.search}${url.hash}`;
      // For purely relative paths without leading /, ensure it starts correctly
      if (href.startsWith('/') && !newHref.startsWith('/')) {
        newHref = `/${newHref}`;
      } else if (!href.startsWith('/') && !href.startsWith('http') && newHref.startsWith('/')) {
        // Handle case where original href was like "somepage" and became "/somepage?network=mainnet"
        // We want to ensure it remains relative if it was relative
        newHref = newHref.substring(1);
      }
    } else {
      newHref = {
        ...href,
        query: {
          ...href.query,
          network: network,
        },
      };
    }

    return (
      <Link href={newHref} {...props} ref={ref}>
        {children}
      </Link>
    );
  }
);

NetworkLink.displayName = 'NetworkLink';
